/**
 * Task #599 — AI Lead Catcher admin settings page.
 *
 * Owns the master kill switch, the six per-channel toggles, the daily
 * conversation cap, the per-conversation token cap, the persona, the
 * qualifying-question editor, the embed snippet for the web widget,
 * and the last-50 conversation log with channel + outcome filters.
 *
 * Wired at `/admin/lead-catcher`. Permission-gated server-side via
 * `adminPanel.feature.brandingSettings`.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, RefreshCcw, Copy } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type Settings = {
  aiLeadCatcherEnabled: boolean;
  aiLeadCatcherWebEnabled: boolean;
  aiLeadCatcherSmsEnabled: boolean;
  aiLeadCatcherMissedCallEnabled: boolean;
  aiLeadCatcherInstagramEnabled: boolean;
  aiLeadCatcherMessengerEnabled: boolean;
  aiLeadCatcherFormEnabled: boolean;
  aiLeadCatcherMaxConversationsPerDay: number;
  aiLeadCatcherMaxTokensPerConversation: number;
  aiLeadCatcherWebWidgetKey: string;
  aiLeadCatcherPersona: string;
  aiLeadCatcherQuestionsJson: { key: string; label: string; required: boolean }[];
  aiLeadCatcherAgencyName: string;
  aiLeadCatcherFollowUpWording: string;
};

type Conversation = {
  id: string;
  channel: string;
  outcome: string;
  capturedEmail: string | null;
  capturedEventDate: string | null;
  capturedEventType: string | null;
  handedOffLeadId: string | null;
  totalTokens: number;
  totalCostCents: number;
  createdAt: string;
};

type SettingsBoolKey = {
  [K in keyof Settings]: Settings[K] extends boolean ? K : never;
}[keyof Settings];

const CHANNELS: { key: SettingsBoolKey; label: string; help: string }[] = [
  { key: "aiLeadCatcherWebEnabled", label: "Website chat", help: "Drop the snippet on your site." },
  { key: "aiLeadCatcherSmsEnabled", label: "SMS", help: "Replies via your existing Twilio number." },
  { key: "aiLeadCatcherMissedCallEnabled", label: "Missed call → SMS", help: "Auto-text after a missed call." },
  { key: "aiLeadCatcherInstagramEnabled", label: "Instagram DMs", help: "Connect IG via Integrations first." },
  { key: "aiLeadCatcherMessengerEnabled", label: "Messenger", help: "Connect a Page via Integrations first." },
  { key: "aiLeadCatcherFormEnabled", label: "Contact form", help: "First-reply on every public form submission." },
];

export default function AdminLeadCatcherPage() {
  const { toast } = useToast();
  const settingsQ = useQuery<Settings>({ queryKey: ["/api/admin/lead-catcher/settings"] });
  const snippetQ = useQuery<{ snippet: string; scriptUrl: string }>({ queryKey: ["/api/admin/lead-catcher/snippet"] });

  // Local editable copy so toggles + question edits feel instant.
  const [draft, setDraft] = useState<Settings | null>(null);
  useEffect(() => {
    if (settingsQ.data && !draft) setDraft(settingsQ.data);
  }, [settingsQ.data, draft]);

  const [filterChannel, setFilterChannel] = useState<string>("all");
  const [filterOutcome, setFilterOutcome] = useState<string>("all");
  const conversationsQ = useQuery<{ conversations: Conversation[] }>({
    queryKey: [
      "/api/admin/lead-catcher/conversations",
      filterChannel === "all" ? undefined : filterChannel,
      filterOutcome === "all" ? undefined : filterOutcome,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterChannel !== "all") params.set("channel", filterChannel);
      if (filterOutcome !== "all") params.set("outcome", filterOutcome);
      const r = await fetch(`/api/admin/lead-catcher/conversations?${params.toString()}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });

  const saveMut = useMutation({
    mutationFn: async (patch: Partial<Settings> & { rotateWidgetKey?: boolean }) => {
      return apiRequest("PATCH", "/api/admin/lead-catcher/settings", patch);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/lead-catcher/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/lead-catcher/snippet"] });
      toast({ title: "Saved" });
    },
    onError: (e: any) => {
      toast({ title: "Save failed", description: e?.message || "Unknown error", variant: "destructive" });
    },
  });

  const dirty = useMemo(() => {
    if (!draft || !settingsQ.data) return false;
    return JSON.stringify(draft) !== JSON.stringify(settingsQ.data);
  }, [draft, settingsQ.data]);

  if (settingsQ.isLoading || !draft) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="status-lead-catcher-loading">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const updateField = <K extends keyof Settings>(k: K, v: Settings[K]) =>
    setDraft((prev) => (prev ? { ...prev, [k]: v } : prev));

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="text-lead-catcher-title">AI Lead Catcher</h1>
          <p className="text-sm text-muted-foreground">
            Auto-respond to inbound inquiries across every channel and capture qualifying info before a rep takes over.
          </p>
        </div>
        <Button
          onClick={() => saveMut.mutate(draft)}
          disabled={!dirty || saveMut.isPending}
          data-testid="button-save-lead-catcher"
        >
          {saveMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save changes
        </Button>
      </div>

      <Card data-testid="card-lead-catcher-master">
        <CardHeader>
          <CardTitle>Master switch</CardTitle>
          <CardDescription>Turn the entire AI Lead Catcher on or off.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Switch
            checked={draft.aiLeadCatcherEnabled}
            onCheckedChange={(v) => updateField("aiLeadCatcherEnabled", v)}
            data-testid="switch-lead-catcher-enabled"
          />
          <span className="text-sm" data-testid="text-lead-catcher-master-state">
            {draft.aiLeadCatcherEnabled ? "Active — replies are sent" : "Disabled — nothing replies"}
          </span>
        </CardContent>
      </Card>

      <Card data-testid="card-lead-catcher-channels">
        <CardHeader>
          <CardTitle>Channels</CardTitle>
          <CardDescription>Toggle individual inbound channels independently of the master switch.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          {CHANNELS.map(({ key, label, help }) => (
            <div key={String(key)} className="flex items-center justify-between gap-3 rounded-lg border p-3">
              <div className="min-w-0">
                <div className="text-sm font-medium" data-testid={`text-channel-${String(key)}`}>{label}</div>
                <div className="text-xs text-muted-foreground">{help}</div>
              </div>
              <Switch
                checked={draft[key]}
                onCheckedChange={(v: boolean) => updateField(key, v)}
                data-testid={`switch-channel-${String(key)}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card data-testid="card-lead-catcher-limits">
        <CardHeader>
          <CardTitle>Spend limits</CardTitle>
          <CardDescription>Hard caps the engine refuses to cross.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="cap-day">Conversations per day</Label>
            <Input
              id="cap-day"
              type="number"
              min={0}
              max={100000}
              value={draft.aiLeadCatcherMaxConversationsPerDay}
              onChange={(e) => updateField("aiLeadCatcherMaxConversationsPerDay", Number(e.target.value || 0))}
              data-testid="input-cap-conversations-per-day"
            />
            <p className="mt-1 text-xs text-muted-foreground">0 disables new conversations.</p>
          </div>
          <div>
            <Label htmlFor="cap-tokens">Tokens per conversation</Label>
            <Input
              id="cap-tokens"
              type="number"
              min={500}
              max={40000}
              value={draft.aiLeadCatcherMaxTokensPerConversation}
              onChange={(e) => updateField("aiLeadCatcherMaxTokensPerConversation", Number(e.target.value || 0))}
              data-testid="input-cap-tokens-per-conversation"
            />
            <p className="mt-1 text-xs text-muted-foreground">After this, the engine hands off (or closes if no contact captured).</p>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-lead-catcher-persona">
        <CardHeader>
          <CardTitle>Persona</CardTitle>
          <CardDescription>One paragraph of how the AI should describe your business.</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={4}
            placeholder="An entertainment-booking agency that books bands and DJs for weddings, corporate events, and private parties."
            value={draft.aiLeadCatcherPersona}
            onChange={(e) => updateField("aiLeadCatcherPersona", e.target.value)}
            data-testid="textarea-persona"
          />
        </CardContent>
      </Card>

      <Card data-testid="card-lead-catcher-handoff-wording">
        <CardHeader>
          <CardTitle>Hand-off wording</CardTitle>
          <CardDescription>
            These two values are interpolated into every confirmation the prospect receives — email, SMS, missed-call apology — so the promise we make is identical no matter which channel they used.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="agency-name">Agency display name</Label>
            <Input
              id="agency-name"
              maxLength={120}
              placeholder="(uses workspace name when blank)"
              value={draft.aiLeadCatcherAgencyName}
              onChange={(e) => updateField("aiLeadCatcherAgencyName", e.target.value)}
              data-testid="input-agency-name"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Shown in the prospect's confirmation email subject and signature.
            </p>
          </div>
          <div>
            <Label htmlFor="follow-up-wording">Follow-up promise wording</Label>
            <Input
              id="follow-up-wording"
              maxLength={120}
              placeholder="within the hour"
              value={draft.aiLeadCatcherFollowUpWording}
              onChange={(e) => updateField("aiLeadCatcherFollowUpWording", e.target.value)}
              data-testid="input-follow-up-wording"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Phrasing dropped into "<em>{`{Rep}`} from {`{Agency}`} will follow up <strong>{draft.aiLeadCatcherFollowUpWording || "within the hour"}</strong>.</em>"
            </p>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-lead-catcher-questions">
        <CardHeader>
          <CardTitle>Qualifying questions</CardTitle>
          <CardDescription>
            The engine always tries to capture email, event date, and event type. Add up to 20 extra questions to capture
            additional info before hand-off.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {draft.aiLeadCatcherQuestionsJson.map((q, idx) => (
            <div key={idx} className="grid gap-2 sm:grid-cols-[140px_1fr_120px_auto]">
              <Input
                value={q.key}
                onChange={(e) => {
                  const copy = [...draft.aiLeadCatcherQuestionsJson];
                  copy[idx] = { ...copy[idx], key: e.target.value };
                  updateField("aiLeadCatcherQuestionsJson", copy);
                }}
                placeholder="key"
                data-testid={`input-question-key-${idx}`}
              />
              <Input
                value={q.label}
                onChange={(e) => {
                  const copy = [...draft.aiLeadCatcherQuestionsJson];
                  copy[idx] = { ...copy[idx], label: e.target.value };
                  updateField("aiLeadCatcherQuestionsJson", copy);
                }}
                placeholder="What should we ask?"
                data-testid={`input-question-label-${idx}`}
              />
              <div className="flex items-center gap-2">
                <Switch
                  checked={q.required}
                  onCheckedChange={(v) => {
                    const copy = [...draft.aiLeadCatcherQuestionsJson];
                    copy[idx] = { ...copy[idx], required: !!v };
                    updateField("aiLeadCatcherQuestionsJson", copy);
                  }}
                  data-testid={`switch-question-required-${idx}`}
                />
                <span className="text-xs text-muted-foreground">Required</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  const copy = draft.aiLeadCatcherQuestionsJson.filter((_, i) => i !== idx);
                  updateField("aiLeadCatcherQuestionsJson", copy);
                }}
                data-testid={`button-remove-question-${idx}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            disabled={draft.aiLeadCatcherQuestionsJson.length >= 20}
            onClick={() =>
              updateField("aiLeadCatcherQuestionsJson", [
                ...draft.aiLeadCatcherQuestionsJson,
                { key: `field_${draft.aiLeadCatcherQuestionsJson.length + 1}`, label: "", required: false },
              ])
            }
            data-testid="button-add-question"
          >
            <Plus className="mr-1 h-4 w-4" /> Add question
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="card-lead-catcher-snippet">
        <CardHeader>
          <CardTitle>Web widget snippet</CardTitle>
          <CardDescription>Drop this script tag in your website's &lt;head&gt; or before &lt;/body&gt;.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            readOnly
            rows={3}
            value={snippetQ.data?.snippet || ""}
            className="font-mono text-xs"
            data-testid="textarea-widget-snippet"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (snippetQ.data?.snippet) {
                  navigator.clipboard.writeText(snippetQ.data.snippet);
                  toast({ title: "Copied to clipboard" });
                }
              }}
              data-testid="button-copy-snippet"
            >
              <Copy className="mr-1 h-4 w-4" /> Copy snippet
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (confirm("Rotate widget key? Existing snippets will stop working.")) {
                  saveMut.mutate({ rotateWidgetKey: true });
                }
              }}
              data-testid="button-rotate-widget-key"
            >
              <RefreshCcw className="mr-1 h-4 w-4" /> Rotate key
            </Button>
            <Badge variant="secondary" data-testid="text-widget-key">{draft.aiLeadCatcherWebWidgetKey}</Badge>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-lead-catcher-conversations">
        <CardHeader>
          <CardTitle>Recent conversations</CardTitle>
          <CardDescription>Last 50 inbound chats across every channel.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Select value={filterChannel} onValueChange={setFilterChannel}>
              <SelectTrigger className="w-40" data-testid="select-filter-channel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All channels</SelectItem>
                <SelectItem value="web">Web</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
                <SelectItem value="missed_call">Missed call</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="messenger">Messenger</SelectItem>
                <SelectItem value="form">Form</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterOutcome} onValueChange={setFilterOutcome}>
              <SelectTrigger className="w-40" data-testid="select-filter-outcome">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All outcomes</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="handoff">Handed off</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="errored">Errored</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Channel</th>
                  <th className="px-3 py-2 text-left">Outcome</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Event</th>
                  <th className="px-3 py-2 text-right">Tokens</th>
                </tr>
              </thead>
              <tbody>
                {conversationsQ.isLoading && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
                )}
                {!conversationsQ.isLoading && (conversationsQ.data?.conversations.length ?? 0) === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No conversations yet.</td></tr>
                )}
                {conversationsQ.data?.conversations.map((c) => (
                  <tr key={c.id} className="border-t" data-testid={`row-conversation-${c.id}`}>
                    <td className="px-3 py-2">{new Date(c.createdAt).toLocaleString()}</td>
                    <td className="px-3 py-2">{c.channel}</td>
                    <td className="px-3 py-2">
                      <Badge variant={c.outcome === "handoff" ? "default" : c.outcome === "blocked" || c.outcome === "errored" ? "destructive" : "secondary"}>
                        {c.outcome}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">{c.capturedEmail || "—"}</td>
                    <td className="px-3 py-2">{[c.capturedEventType, c.capturedEventDate].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="px-3 py-2 text-right">{c.totalTokens.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
